import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { navigate } from 'vike/client/router';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { Button } from '@/components/UI/Button';
import useMe from '@/renderer/utils/useMe';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

type MailingListData = { email: string };

const mailingListRequest: RequestSchema<MailingListData> = {
  name: 'mailingListSubscribeRequest',
  input: '/api/v1/mailingList',
  method: 'POST',
};

export default function MailingListForm({
  guarded,
  autoFocus = true,
  compact = false,
}: {
  guarded: boolean;
  autoFocus?: boolean;
  compact?: boolean;
}) {
  const form = useForm<WithFormErrors<MailingListData>>();
  const {
    formState: { isSubmitSuccessful },
    watch,
  } = form;

  const [_me] = useMe(true);
  const request = useRequest(mailingListRequest, { manual: true, hideAlert: true });
  const redirectTimeout = useRef<NodeJS.Timeout>();

  // Prefill email for redirect
  const emailValue = watch('email');

  // After successful signup, redirect to register with email prefilled
  useEffect(() => {
    if (isSubmitSuccessful && emailValue) {
      redirectTimeout.current = setTimeout(() => {
        navigate(`/register?email=${encodeURIComponent(emailValue)}`);
      }, 1200); // Show success for a moment before redirect
    }
    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, [isSubmitSuccessful, emailValue]);

  // Form submit handler
  const handleFormSubmit = useFormSubmit({
    form,
    callback: async (body) => {
      await request.run(body, { throwOnRun: true });
      // Success handled by useEffect above
    },
  });

  // Render

  if (compact) {
    return (
      <form onSubmit={handleFormSubmit} method="POST" className="flex gap-2">
        <input
          type="email"
          placeholder="Email address"
          autoComplete="email"
          autoFocus={autoFocus}
          {...form.register('email', { required: true })}
          className="flex-1 rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950/10 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:placeholder:text-zinc-500"
        />
        <button
          type="submit"
          disabled={request.loading || isSubmitSuccessful}
          className="shrink-0 rounded-sm px-4 py-2 text-xs font-semibold text-zinc-200 ring-1 ring-white/20 ring-inset transition hover:bg-white/5 hover:text-white disabled:opacity-60"
        >
          {isSubmitSuccessful ? '✓' : 'Notify me'}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} method="POST" className="space-y-6">
      <FormControl
        form={form}
        name="email"
        label="Email address"
        autoComplete="email"
        required
        autoFocus={autoFocus}
        autoCapitalize="off"
        autoCorrect="off"
        autoSave="off"
      />

      <FormErrors form={form} request={request} />

      <FormSubmitButton
        disabled={request.loading}
        loading={request.loading || isSubmitSuccessful}
        className="w-full"
      >
        {(isSubmitSuccessful && 'Successfully joined mailing list...') || 'Join mailing list'}
      </FormSubmitButton>

      {!!guarded && (
        <div className="flex items-center justify-center space-x-2 self-center">
          <Button variant="ghost" onClick={() => history.back()}>
            Or return to previous page <ArrowRightIcon height={16} className="size-4" />
          </Button>
        </div>
      )}
    </form>
  );
}
